import React from 'react';
import { useTranslation } from 'react-i18next';
import CriticalityBadge from './CriticalityBadge';
import DescriptionBanner from './DescriptionBanner';
import TotalsSummaryCard from './TotalSummaryCard';
import CountUpAnimation from './UI/CountUpAnimation';


export default function CyberSurveillanceDashboard({ cyberSurveillanceResult, viewDescriptionBanner = false }) {
    const { t } = useTranslation();


    const totalValue = [
        { label: t('cyber_surveillance.total_breaches'), value: cyberSurveillanceResult?.results?.meta?.summary?.total_breaches || 0 },
        { label: t('cyber_surveillance.total_exposed_ips'), value: cyberSurveillanceResult?.results?.meta?.summary?.total_exposed_ips || 0 },
        { label: t('cyber_surveillance.total_technologies'), value: cyberSurveillanceResult?.results?.meta?.summary?.total_technologies || 0 },
        { label: t('cyber_surveillance.total_subdomains'), value: cyberSurveillanceResult?.results?.meta?.summary?.total_subdomains || 0 },
    ];

    const stats = [
        { value: cyberSurveillanceResult?.results?.meta?.summary?.total_vulnerabilities || 0, label: t('cyber_surveillance.threats_found'), color: 'text-red-600' },
        // { value: 45, label: 'Da risolvere', color: 'text-yellow-500' },
        // { value: 55, label: 'Risolte', color: 'text-green-600' },
    ];

    const badges = [
        { label: t('cyber_surveillance.badge_exposed_credentials'), level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.emails?.risk_level, color: 'bg-red-50 text-red-600' },
        { label: t('cyber_surveillance.badge_ip_vulnerabilities'), level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.ips?.risk_level, color: 'bg-red-50 text-red-600' },
        { label: t('cyber_surveillance.badge_domain_vulnerabilities'), level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.domains?.risk_level, color: 'bg-green-50 text-green-600' },
    ];

    return (
        <div className="rounded-lg w-full h-full mx-auto flex flex-col">

            {viewDescriptionBanner ?
                <div className='mb-6'>
                    <DescriptionBanner title={t('cyber_surveillance.title')} description={t('cyber_surveillance.description')} />
                </div> : null
            }

            <TotalsSummaryCard items={totalValue} />
        </div>

    );
}

function CyberSurveillanceStats({ cyberSurveillanceResult }) {
    const { t } = useTranslation();

    const stats = [
        { value: cyberSurveillanceResult?.results?.meta?.summary?.total_vulnerabilities || 0, label: t('cyber_surveillance.threats_found'), color: 'text-red-600' },
    ];

    // Funzione per calcolare il livello di rischio (0-100)
    const getRiskValue = (level) => {
        const riskLevels = {
            'low': 20,
            'medium': 50,
            'high': 75,
            'critical': 90,
            'info': 10
        };
        return riskLevels[level?.toLowerCase()] || 0;
    };

    const riskIndicators = [
        { 
            label: t('cyber_surveillance.badge_exposed_credentials'), 
            level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.emails?.risk_level,
            value: getRiskValue(cyberSurveillanceResult?.results?.meta?.summary?.groups?.emails?.risk_level),
            count: cyberSurveillanceResult?.results?.meta?.summary?.groups?.emails?.count || 0
        },
        { 
            label: t('cyber_surveillance.badge_ip_vulnerabilities'), 
            level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.ips?.risk_level,
            value: getRiskValue(cyberSurveillanceResult?.results?.meta?.summary?.groups?.ips?.risk_level),
            count: cyberSurveillanceResult?.results?.meta?.summary?.groups?.ips?.count || 0
        },
        { 
            label: t('cyber_surveillance.badge_domain_vulnerabilities'), 
            level: cyberSurveillanceResult?.results?.meta?.summary?.groups?.domains?.risk_level,
            value: getRiskValue(cyberSurveillanceResult?.results?.meta?.summary?.groups?.domains?.risk_level),
            count: cyberSurveillanceResult?.results?.meta?.summary?.groups?.domains?.count || 0
        },
    ];

    // Funzione per ottenere il colore del gauge basato sul valore
    const getGaugeColor = (value) => {
        if (value <= 25) return '#22c55e'; // verde
        if (value <= 50) return '#eab308'; // giallo
        if (value <= 75) return '#f97316'; // arancione
        return '#ef4444'; // rosso
    };

    return (
        <>
            {/* Card 1: Minacce rilevate */}
            <div className="rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200 bg-white hover:shadow-xl transition-shadow duration-200 flex flex-col items-center justify-center col-span-1">
                {stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                        <span className={`${stat.color} font-bold text-3xl sm:text-5xl md:text-6xl block`}>
                            <CountUpAnimation end={stat.value} />
                        </span>
                        <span className="uppercase text-[10px] sm:text-xs md:text-sm text-gray-600 mt-2 sm:mt-3 font-semibold block break-words">
                            {stat.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Card 2-4: Risk Indicators Gauges */}
            {riskIndicators.map((indicator, idx) => (
                <div key={idx} className="rounded-xl shadow-lg p-3 sm:p-6 border border-gray-200 bg-white hover:shadow-xl transition-shadow duration-200 flex flex-col items-center justify-between col-span-1">
                    <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3 w-full flex-1">
                        <GaugeChart 
                            value={indicator.value}
                        />
                        <div className="text-center w-full flex flex-col items-center">
                            <p className="text-[10px] sm:text-xs font-semibold text-gray-700 uppercase mb-1 sm:mb-2 break-words leading-tight">
                                {indicator.label}
                            </p>
                            <span className="text-xl sm:text-2xl font-bold text-gray-800 block mb-1 sm:mb-2">
                                {indicator.count}
                            </span>
                            <CriticalityBadge livello={indicator.level} />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
}

// Componente Gauge Chart
function GaugeChart({ value }) {
    // Responsive sizing via container width
    const size = 120; // base size; SVG scales with CSS width
    const strokeWidth = 12;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size - strokeWidth) / 2 - 3;
    
    // Calcola l'angolo della lancetta (da 180° a 0°, in senso orario)
    const angle = 180 - (value / 100) * 180;
    
    // Coordinate finali della lancetta usando trigonometria
    const needleLength = radius - 10;
    const angleRad = (angle * Math.PI) / 180;
    const needleX = centerX + needleLength * Math.cos(angleRad);
    const needleY = centerY - needleLength * Math.sin(angleRad);
    
    // Funzione per creare un arco
    const createArc = (startAngle, endAngle) => {
        const start = {
            x: centerX + radius * Math.cos((startAngle * Math.PI) / 180),
            y: centerY - radius * Math.sin((startAngle * Math.PI) / 180)
        };
        const end = {
            x: centerX + radius * Math.cos((endAngle * Math.PI) / 180),
            y: centerY - radius * Math.sin((endAngle * Math.PI) / 180)
        };
        return `M ${start.x} ${start.y} A ${radius} ${radius} 0 0 1 ${end.x} ${end.y}`;
    };
    
    // Settori colorati
    const sectors = [
        { start: 180, end: 135, color: '#22c55e' }, // Verde
        { start: 135, end: 90, color: '#facc15' },  // Giallo
        { start: 90, end: 45, color: '#fb923c' },   // Arancione
        { start: 45, end: 0, color: '#ef4444' }     // Rosso
    ];
    
    return (
        <div className="flex justify-center w-24 sm:w-28 lg:w-32">
            <svg width="100%" viewBox={`0 0 ${size} ${size / 2 + 15}`} preserveAspectRatio="xMidYMid meet">
                {/* Archi colorati a settori */}
                {sectors.map((sector, idx) => (
                    <path
                        key={idx}
                        d={createArc(sector.start, sector.end)}
                        fill="none"
                        stroke={sector.color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="butt"
                    />
                ))}
                
                {/* Lancetta */}
                <line
                    x1={centerX}
                    y1={centerY}
                    x2={needleX}
                    y2={needleY}
                    stroke="#374151"
                    strokeWidth="3"
                    strokeLinecap="round"
                    style={{ transition: 'all 0.8s ease' }}
                />
                
                {/* Cerchio centrale */}
                <circle cx={centerX} cy={centerY} r="6" fill="#374151" />
                <circle cx={centerX} cy={centerY} r="3" fill="#ffffff" />
            </svg>
        </div>
    );
}

export { CyberSurveillanceStats };